import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Heart, Search } from "lucide-react";
import { toast } from "sonner";
import customerApi from "./customerApi";
import { SORT_OPTIONS, distinctColours, applyCatalogControls } from "../hooks/catalogSort";

const AUBERGINE  = "var(--hq-deep, #5A4565)";
const PLUM       = "var(--hq-accent, #6F4A7A)";
const PARCH_EDGE = "#EFE4CC";
const GOLD = "#C9A268";

export default function HQCatalog() {
  const [varieties, setVarieties] = useState([]);
  const [wishIds, setWishIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | wishlist
  const [sort, setSort] = useState("az");
  const [colour, setColour] = useState("all");

  useEffect(() => {
    Promise.all([
      customerApi.get("/catalog"),
      customerApi.get("/customer/wishlist").catch(() => ({ data: { variety_ids: [] } })),
    ]).then(([cat, wl]) => {
      setVarieties(Array.isArray(cat.data) ? cat.data : (cat.data?.varieties || []));
      setWishIds(new Set(wl.data?.variety_ids || []));
    }).finally(() => setLoading(false));
  }, []);

  const colours = useMemo(() => distinctColours(varieties), [varieties]);
  const visible = useMemo(() => {
    let list = varieties;
    if (filter === "wishlist") list = list.filter((v) => wishIds.has(v.id));
    return applyCatalogControls(list, { q, sort, colour });
  }, [varieties, wishIds, q, filter, sort, colour]);

  const toggle = async (id) => {
    // Optimistic
    const next = new Set(wishIds);
    const willAdd = !next.has(id);
    if (willAdd) next.add(id); else next.delete(id);
    setWishIds(next);
    try {
      await customerApi.post("/customer/wishlist/toggle", { variety_id: id });
    } catch {
      // revert
      const back = new Set(wishIds);
      setWishIds(back);
      toast.error("Couldn't update wishlist.");
    }
  };

  return (
    <div data-testid="hq-catalog" className="space-y-5" style={{ color: AUBERGINE }}>
      <header>
        <div className="text-[10px] tracking-[0.45em] uppercase font-semibold" style={{ color: PLUM }}>✦ Heritage Headquarters</div>
        <h1 className="text-4xl mt-1.5">Catalogue &amp; Wishlist</h1>
        <p className="italic opacity-75 text-sm mt-1">Browse every named variety. Tap the heart to save it to your wishlist , Kayla can see it too.</p>
      </header>

      <div className="flex flex-col md:flex-row gap-2">
        <div className="flex items-center gap-2 bg-white border rounded px-3 py-2 flex-1" style={{ borderColor: PARCH_EDGE }}>
          <Search className="w-4 h-4 opacity-60" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="hq-catalog-search"
            placeholder="Search variety name…"
            className="flex-1 bg-transparent outline-none text-sm"
            style={{ color: AUBERGINE }}
          />
        </div>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          data-testid="hq-catalog-sort"
          className="px-3 py-2 text-sm bg-white border rounded"
          style={{ borderColor: PARCH_EDGE, color: AUBERGINE }}
        >
          {SORT_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
        </select>
        <select
          value={colour}
          onChange={(e) => setColour(e.target.value)}
          data-testid="hq-catalog-colour"
          className="px-3 py-2 text-sm bg-white border rounded max-w-full"
          style={{ borderColor: PARCH_EDGE, color: AUBERGINE }}
        >
          <option value="all">All colours</option>
          {colours.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <div className="inline-flex border rounded overflow-hidden" style={{ borderColor: PARCH_EDGE }}>
          <FilterBtn active={filter === "all"} onClick={() => setFilter("all")} testid="hq-catalog-filter-all">All</FilterBtn>
          <FilterBtn active={filter === "wishlist"} onClick={() => setFilter("wishlist")} testid="hq-catalog-filter-wishlist">My Wishlist ({wishIds.size})</FilterBtn>
        </div>
      </div>

      {loading ? (
        <div className="opacity-70"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Loading catalogue…</div>
      ) : visible.length === 0 ? (
        <div className="bg-white border border-dashed rounded p-10 text-center italic opacity-70" style={{ borderColor: PARCH_EDGE }} data-testid="hq-catalog-empty">
          {filter === "wishlist" ? "Your wishlist is empty , tap a heart to save varieties." : "No varieties match."}
        </div>
      ) : (
        <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="hq-catalog-grid">
          {visible.map((v) => {
            const inWish = wishIds.has(v.id);
            return (
              <li key={v.id} className="bg-white border rounded overflow-hidden flex flex-col" style={{ borderColor: PARCH_EDGE }} data-testid={`hq-catalog-card-${v.id}`}>
                {v.image_url ? (
                  <div className="aspect-square bg-cover bg-center" style={{ backgroundImage: `url(${v.image_url})` }} />
                ) : (
                  <div className="aspect-square bg-[#F4ECD8] flex items-center justify-center text-[#9C8B73] italic text-xs">No image</div>
                )}
                <div className="p-3 flex-1 flex flex-col">
                  <div className="text-sm font-semibold truncate" style={{ color: AUBERGINE }}>{v.name}</div>
                  {v.category && <div className="text-[10px] tracking-[0.2em] uppercase opacity-70 mt-0.5">{v.category}</div>}
                  <button
                    type="button"
                    onClick={() => toggle(v.id)}
                    data-testid={`hq-wishlist-toggle-${v.id}`}
                    className="mt-2 inline-flex items-center justify-center gap-1.5 px-2 py-1 rounded text-[10px] tracking-[0.2em] uppercase font-semibold border transition"
                    style={{ borderColor: inWish ? "#A33030" : PARCH_EDGE, color: inWish ? "#A33030" : AUBERGINE, backgroundColor: inWish ? "#FBE7E7" : "white" }}
                  >
                    <Heart className="w-3 h-3" fill={inWish ? "#A33030" : "none"} />
                    {inWish ? "Saved" : "Save"}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function FilterBtn({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="px-3 py-2 text-[10px] tracking-[0.25em] uppercase font-semibold transition"
      style={{ backgroundColor: active ? AUBERGINE : "white", color: active ? "#FBF6E9" : AUBERGINE }}
    >
      {children}
    </button>
  );
}
